import type { ListQueryParams, ListRequest } from "./pagination";

/**
 * Query keys centralisées (règle : jamais de clé en dur dans les features).
 *
 * Squelettes par domaine du guide (§1.3) : chaque ressource expose
 * `list(params)` pour les listes `POST .../all` (corps `ListRequest`,
 * §4.2) et `detail(id)` pour les lectures par identifiant. Ajouter les
 * clés spécifiques (sous-ressources, balances…) à mesure des phases.
 *
 * Listes paginées en GET (sous-ressources marchand, templates, configs) :
 * les `ListQueryParams` viennent APRÈS l'identifiant et sont OMIS quand absents —
 * ainsi les invalidations `KEY(id)` restent un préfixe exact des clés
 * paginées (jamais d'`undefined` en fin de clé).
 */
export const QUERY_KEYS = {
  // auth — user-service (mutations publiques ; pas de GET profil)
  AUTH_CURRENT_USER: () => ["AUTH_CURRENT_USER"] as const,
  AUTH_LOGIN: () => ["AUTH_LOGIN"] as const,
  AUTH_REFRESH: () => ["AUTH_REFRESH"] as const,
  AUTH_LOGOUT: () => ["AUTH_LOGOUT"] as const,
  AUTH_FORGOT_PASSWORD: () => ["AUTH_FORGOT_PASSWORD"] as const,
  AUTH_ADD_USER: () => ["AUTH_ADD_USER"] as const,

  // kyc — KYC & Identités
  KYC_PARTIES_LIST: (params?: ListRequest) => ["KYC_PARTIES_LIST", params] as const,
  KYC_PARTY_DETAIL: (partyId: string) => ["KYC_PARTY_DETAIL", partyId] as const,
  KYC_CASES_LIST: (params?: ListRequest) => ["KYC_CASES_LIST", params] as const,
  KYC_CASE_DETAIL: (kycCaseId: string) => ["KYC_CASE_DETAIL", kycCaseId] as const,
  KYC_AGENTS_LIST: (params?: ListRequest) => ["KYC_AGENTS_LIST", params] as const,
  KYC_AGENT_DETAIL: (agentId: string) => ["KYC_AGENT_DETAIL", agentId] as const,
  KYC_COMPANY_PROFILE: (partyId: string) => ["KYC_COMPANY_PROFILE", partyId] as const,
  // Phase 14 — Delta v2.3 (lookup noms) / Phase 15 — Delta v2.4 (lookup agent)
  KYC_PARTY_NAMES: (partyIds: readonly string[]) =>
    ["KYC_PARTY_NAMES", [...partyIds].sort().join(",")] as const,
  KYC_AGENT_BY_KEYCLOAK: (keycloakUserId: string) =>
    ["KYC_AGENT_BY_KEYCLOAK", keycloakUserId] as const,

  // wallet — Portefeuilles & Transactions
  WALLET_LIST: (params?: ListRequest) => ["WALLET_LIST", params] as const,
  WALLET_DETAIL: (walletId: string) => ["WALLET_DETAIL", walletId] as const,
  WALLET_BALANCE: (walletId: string) => ["WALLET_BALANCE", walletId] as const,
  WALLET_TRANSACTIONS_LIST: (params?: ListRequest) =>
    ["WALLET_TRANSACTIONS_LIST", params] as const,
  WALLET_TRANSACTION_DETAIL: (transactionId: string) =>
    ["WALLET_TRANSACTION_DETAIL", transactionId] as const,
  WALLET_BENEFICIARIES: (walletId: string) => ["WALLET_BENEFICIARIES", walletId] as const,
  WALLET_EMONEY_RECONCILIATION: (currency: string) =>
    ["WALLET_EMONEY_RECONCILIATION", currency] as const,
  WALLET_QR_ME: (walletId: string) => ["WALLET_QR_ME", walletId] as const,
  WALLET_QR_RESOLVE: (token: string) => ["WALLET_QR_RESOLVE", token] as const,
  // Phase 15 — Delta v2.4 : QR agent statique (`GET /WalletQrCodes/agent/me`).
  WALLET_QR_AGENT_ME: () => ["WALLET_QR_AGENT_ME"] as const,
  // Phase 18.3 — Delta v3.1 : contestations de transfert (TransactionDisputes).
  WALLET_TRANSACTION_DISPUTES_LIST: (params?: ListRequest) =>
    ["WALLET_TRANSACTION_DISPUTES_LIST", params] as const,
  WALLET_TRANSACTION_DISPUTE_DETAIL: (transactionDisputeId: string) =>
    ["WALLET_TRANSACTION_DISPUTE_DETAIL", transactionDisputeId] as const,

  // savings — Coffres d'épargne (Phase 18, guide v3.1 §6.10)
  SAVINGS_VAULTS_LIST: (params?: ListRequest) =>
    ["SAVINGS_VAULTS_LIST", params] as const,
  SAVINGS_VAULT_DETAIL: (vaultId: string) =>
    ["SAVINGS_VAULT_DETAIL", vaultId] as const,
  SAVINGS_VAULT_MOVEMENTS: (vaultId: string, params?: ListQueryParams) =>
    params
      ? (["SAVINGS_VAULT_MOVEMENTS", vaultId, params] as const)
      : (["SAVINGS_VAULT_MOVEMENTS", vaultId] as const),
  SAVINGS_VAULT_LOCK_EVENTS: (vaultId: string, params?: ListQueryParams) =>
    params
      ? (["SAVINGS_VAULT_LOCK_EVENTS", vaultId, params] as const)
      : (["SAVINGS_VAULT_LOCK_EVENTS", vaultId] as const),
  SAVINGS_SUMMARY: () => ["SAVINGS_SUMMARY"] as const,

  // ledger — Comptabilité (Phase 6, guide §6.4)
  LEDGER_JOURNAL_ENTRIES_LIST: (params?: ListRequest) =>
    ["LEDGER_JOURNAL_ENTRIES_LIST", params] as const,
  LEDGER_JOURNAL_ENTRY_DETAIL: (journalEntryId: string) =>
    ["LEDGER_JOURNAL_ENTRY_DETAIL", journalEntryId] as const,
  LEDGER_CHART_OF_ACCOUNTS_LIST: (params?: ListRequest) =>
    ["LEDGER_CHART_OF_ACCOUNTS_LIST", params] as const,
  LEDGER_CHART_OF_ACCOUNT_DETAIL: (chartOfAccountId: string) =>
    ["LEDGER_CHART_OF_ACCOUNT_DETAIL", chartOfAccountId] as const,
  LEDGER_POSTING_TEMPLATES_LIST: (params?: ListRequest) =>
    ["LEDGER_POSTING_TEMPLATES_LIST", params] as const,
  LEDGER_POSTING_TEMPLATE_DETAIL: (postingTemplateId: string) =>
    ["LEDGER_POSTING_TEMPLATE_DETAIL", postingTemplateId] as const,
  LEDGER_PRODUCT_CODES_LIST: (params?: ListRequest) =>
    ["LEDGER_PRODUCT_CODES_LIST", params] as const,
  LEDGER_PRODUCT_CODE_DETAIL: (productCodeId: string) =>
    ["LEDGER_PRODUCT_CODE_DETAIL", productCodeId] as const,
  LEDGER_PRODUCT_CODE_BY_CODE: (code: string) =>
    ["LEDGER_PRODUCT_CODE_BY_CODE", code] as const,
  LEDGER_PROVIDERS_LIST: (params?: ListRequest) =>
    ["LEDGER_PROVIDERS_LIST", params] as const,
  LEDGER_PROVIDER_DETAIL: (providerId: string) =>
    ["LEDGER_PROVIDER_DETAIL", providerId] as const,
  LEDGER_PRODUCT_PROVIDER_MAPPINGS_LIST: (params?: ListRequest) =>
    ["LEDGER_PRODUCT_PROVIDER_MAPPINGS_LIST", params] as const,
  LEDGER_PRODUCT_PROVIDER_MAPPING_DETAIL: (productProviderMappingId: string) =>
    [
      "LEDGER_PRODUCT_PROVIDER_MAPPING_DETAIL",
      productProviderMappingId,
    ] as const,
  LEDGER_FX_RATES_LIST: (params?: ListRequest) =>
    ["LEDGER_FX_RATES_LIST", params] as const,

  // fee-engine — Frais & Tarification
  FEE_RULES_LIST: (params?: ListRequest) => ["FEE_RULES_LIST", params] as const,
  FEE_RULE_DETAIL: (feeRuleId: string) => ["FEE_RULE_DETAIL", feeRuleId] as const,
  FEE_PROMOTIONS_LIST: (params?: ListRequest) => ["FEE_PROMOTIONS_LIST", params] as const,
  FEE_PROMOTION_DETAIL: (promotionId: string) =>
    ["FEE_PROMOTION_DETAIL", promotionId] as const,
  FEE_SUBSCRIPTION_PLANS_LIST: (params?: ListRequest) =>
    ["FEE_SUBSCRIPTION_PLANS_LIST", params] as const,
  FEE_SUBSCRIPTION_PLAN_DETAIL: (planId: string) =>
    ["FEE_SUBSCRIPTION_PLAN_DETAIL", planId] as const,
  FEE_COMPANY_SUBSCRIPTIONS_LIST: (params?: ListRequest) =>
    ["FEE_COMPANY_SUBSCRIPTIONS_LIST", params] as const,
  FEE_COMPANY_SUBSCRIPTION_DETAIL: (companySubscriptionId: string) =>
    ["FEE_COMPANY_SUBSCRIPTION_DETAIL", companySubscriptionId] as const,

  // merchant — Marchands & QR
  MERCHANTS_LIST: (params?: ListRequest) => ["MERCHANTS_LIST", params] as const,
  MERCHANT_DETAIL: (merchantId: string) => ["MERCHANT_DETAIL", merchantId] as const,
  MERCHANT_SUB_MERCHANTS: (
    masterId: string,
    params?: ListQueryParams & { actingMerchantId?: string }
  ) =>
    params
      ? (["MERCHANT_SUB_MERCHANTS", masterId, params] as const)
      : (["MERCHANT_SUB_MERCHANTS", masterId] as const),
  MERCHANT_STORES_BY_MERCHANT: (merchantId: string, params?: ListQueryParams) =>
    params
      ? (["MERCHANT_STORES_BY_MERCHANT", merchantId, params] as const)
      : (["MERCHANT_STORES_BY_MERCHANT", merchantId] as const),
  MERCHANT_STORE_DETAIL: (storeId: string) =>
    ["MERCHANT_STORE_DETAIL", storeId] as const,
  MERCHANT_QR_CODES_BY_MERCHANT: (merchantId: string, params?: ListQueryParams) =>
    params
      ? (["MERCHANT_QR_CODES_BY_MERCHANT", merchantId, params] as const)
      : (["MERCHANT_QR_CODES_BY_MERCHANT", merchantId] as const),
  MERCHANT_QR_CODE_BY_TOKEN: (payloadToken: string) =>
    ["MERCHANT_QR_CODE_BY_TOKEN", payloadToken] as const,
  MERCHANT_SALES_AGENTS_BY_MERCHANT: (merchantId: string, params?: ListQueryParams) =>
    params
      ? (["MERCHANT_SALES_AGENTS_BY_MERCHANT", merchantId, params] as const)
      : (["MERCHANT_SALES_AGENTS_BY_MERCHANT", merchantId] as const),
  MERCHANT_SALES_AGENT_DETAIL: (salesAgentId: string) =>
    ["MERCHANT_SALES_AGENT_DETAIL", salesAgentId] as const,
  MERCHANT_SALES_AGENT_ME: () => ["MERCHANT_SALES_AGENT_ME"] as const,
  MERCHANT_SALES_LIST: (params?: ListRequest) =>
    ["MERCHANT_SALES_LIST", params] as const,
  MERCHANT_SALES_BY_AGENT: (salesAgentId: string) =>
    ["MERCHANT_SALES_BY_AGENT", salesAgentId] as const,
  MERCHANT_SETTLEMENT_CONFIG: (merchantId: string) =>
    ["MERCHANT_SETTLEMENT_CONFIG", merchantId] as const,
  MERCHANT_FEE_OVERRIDES: (merchantId: string, params?: ListQueryParams) =>
    params
      ? (["MERCHANT_FEE_OVERRIDES", merchantId, params] as const)
      : (["MERCHANT_FEE_OVERRIDES", merchantId] as const),

  // notification — Notifications
  NOTIFICATION_TEMPLATES_LIST: (params?: ListQueryParams) =>
    params
      ? (["NOTIFICATION_TEMPLATES_LIST", params] as const)
      : (["NOTIFICATION_TEMPLATES_LIST"] as const),
  NOTIFICATION_TEMPLATE_DETAIL: (
    code: string,
    channel: string,
    language: string
  ) => ["NOTIFICATION_TEMPLATE_DETAIL", code, channel, language] as const,
  NOTIFICATION_PREFERENCES: (partyId: string) =>
    ["NOTIFICATION_PREFERENCES", partyId] as const,
  NOTIFICATION_LOGS_LIST: (params?: ListRequest) =>
    ["NOTIFICATION_LOGS_LIST", params] as const,
  // Phase 14 — Delta v2.3
  NOTIFICATION_PROVIDERS_LIST: (params?: ListRequest) =>
    ["NOTIFICATION_PROVIDERS_LIST", params] as const,
  NOTIFICATION_PROVIDER_DETAIL: (notificationProviderId: string) =>
    ["NOTIFICATION_PROVIDER_DETAIL", notificationProviderId] as const,

  // admin — Administration & double validation
  ADMIN_APPROVAL_REQUESTS_LIST: (params?: ListRequest) =>
    ["ADMIN_APPROVAL_REQUESTS_LIST", params] as const,
  ADMIN_APPROVAL_REQUEST_DETAIL: (approvalRequestId: string) =>
    ["ADMIN_APPROVAL_REQUEST_DETAIL", approvalRequestId] as const,
  ADMIN_AUDIT_LOGS_LIST: (params?: ListRequest) =>
    ["ADMIN_AUDIT_LOGS_LIST", params] as const,
  // Phase 8 — SystemConfigs & ApiClients (gateway)
  ADMIN_SYSTEM_CONFIGS_LIST: (params?: ListQueryParams) =>
    params
      ? (["ADMIN_SYSTEM_CONFIGS_LIST", params] as const)
      : (["ADMIN_SYSTEM_CONFIGS_LIST"] as const),
  ADMIN_SYSTEM_CONFIG_DETAIL: (key: string) =>
    ["ADMIN_SYSTEM_CONFIG_DETAIL", key] as const,
  ADMIN_API_CLIENTS_LIST: (params?: ListRequest) =>
    ["ADMIN_API_CLIENTS_LIST", params] as const,
  ADMIN_API_CLIENT_DETAIL: (apiClientId: string) =>
    ["ADMIN_API_CLIENT_DETAIL", apiClientId] as const,

  // Phase 9 — Paie & Cagnottes (types de transaction du wallet-service §6.3 :
  // les listes réutilisent WALLET_TRANSACTIONS_LIST avec le filtre `type`
  // verrouillé ; squelettes réservés pour d'éventuelles vues dédiées).
  PAYROLL_RUNS_LIST: (params?: ListRequest) =>
    ["PAYROLL_RUNS_LIST", params] as const,
  FUNDRAISING_CONTRIBUTIONS_LIST: (params?: ListRequest) =>
    ["FUNDRAISING_CONTRIBUTIONS_LIST", params] as const,

  // Phase 22 — Paie de masse (payroll-service v4.1, §6.11)
  PAYROLL_BATCHES_LIST: (params?: ListRequest) =>
    ["PAYROLL_BATCHES_LIST", params] as const,
  PAYROLL_BATCH_DETAIL: (batchId: string) =>
    ["PAYROLL_BATCH_DETAIL", batchId] as const,
  PAYROLL_BATCH_STATS: (from?: string, to?: string) =>
    ["PAYROLL_BATCH_STATS", from, to] as const,

  // Phase 16 — OutboxEvents (guide v2.5, 6 services)
  OUTBOX_EVENTS_LIST: (
    service: string,
    params?: { page?: number; pageSize?: number }
  ) => ["OUTBOX_EVENTS_LIST", service, params] as const,

  // Phase 11 — Reporting fédéré (guide §7)
  REPORTING_OVERVIEW: (from?: string, to?: string) =>
    ["REPORTING_OVERVIEW", from, to] as const,
  REPORTING_SUMMARY: (service: string, from?: string, to?: string) =>
    ["REPORTING_SUMMARY", service, from, to] as const,
  REPORTING_SERIES: (
    service: string,
    params?: {
      from?: string;
      to?: string;
      metricKey?: string;
      currency?: string;
    }
  ) => ["REPORTING_SERIES", service, params] as const,
  REPORTING_GAUGES: (service: string) => ["REPORTING_GAUGES", service] as const,
} as const;
